/**
 * Demo personas (19 Oct): the cast, starting a persona in this tab, going back,
 * and the demo's UAT data (prepare / reset). Every switch resets every cached
 * read, so nothing of one identity is ever shown under the other.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createDemoCase,
  type DemoCaseResponse,
  type DemoPersonaList,
  type DemoSteps,
  type HrPortalFiled,
  hrPortalFileLeaver,
  hrPortalFileNewUser,
  type HrPortalLeaver,
  type HrPortalNewUser,
  hrPortalPickUp,
  listDemoPersonas,
  prepareDemoPersonas,
  resetDemoPersonas,
  startDemoPersona,
} from "@/lib/api";
import type { DemoRole } from "@/lib/demo-persona";
import { clearDemoPersona, setDemoPersona } from "@/lib/demo-persona-store";
import { logger } from "@/lib/logger";

/** Who plays which role (the admin's own call, from any tab). */
export function useDemoPersonas(enabled: boolean) {
  return useQuery({
    queryKey: ["demo-personas"],
    queryFn: async (): Promise<DemoPersonaList> => {
      const { data } = await listDemoPersonas({ throwOnError: true });
      return data;
    },
    enabled,
    staleTime: 60_000,
  });
}

/** Play `employeeId` as `role` in THIS tab only. */
export function useStartDemoPersona() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeId: string; role: DemoRole }) => {
      logger.debug("[demo] start", input.role);
      const { data } = await startDemoPersona({
        body: { employee_id: input.employeeId, role: input.role },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      setDemoPersona({
        token: data.token,
        role: (data.persona.demo_role ?? "other") as DemoRole,
        name: data.persona.name,
        email: data.persona.email,
        can_act: data.persona.can_act === true,
        expires_at: data.persona.expires_at,
      });
      await queryClient.resetQueries();
    },
  });
}

/** Back to me in this tab (other tabs keep their personas). */
export function useBackToMe(): () => void {
  const queryClient = useQueryClient();
  return () => {
    clearDemoPersona();
    void queryClient.resetQueries();
  };
}

/** Put the cast in place on UAT (idempotent). */
export function usePrepareDemo() {
  return useMutation({
    mutationFn: async (): Promise<DemoSteps> => {
      const { data } = await prepareDemoPersonas({ throwOnError: true });
      return data;
    },
  });
}

/** Cancel the demo case; optionally undo the setup. */
export function useResetDemo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { caseNumber: string; undoSetup: boolean }): Promise<DemoSteps> => {
      const { data } = await resetDemoPersonas({
        body: { case_number: input.caseNumber || null, undo_setup: input.undoSetup },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries(),
  });
}

/** Prepare, then a fresh demo New user past the gate (refused while one is open). */
export function useCreateDemoCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DemoCaseResponse> => {
      const { data } = await createDemoCase({ throwOnError: true });
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries(),
  });
}

/** File as the HR Portal (28 Oct): a New user or a Leaver, exactly as Timo's portal files it. */
export function useHrPortalFile() {
  return useMutation({
    mutationFn: async (
      input: { kind: "new"; body: HrPortalNewUser } | { kind: "leave"; body: HrPortalLeaver },
    ): Promise<HrPortalFiled> => {
      if (input.kind === "new") {
        const { data } = await hrPortalFileNewUser({ body: input.body, throwOnError: true });
        return data;
      }
      const { data } = await hrPortalFileLeaver({ body: input.body, throwOnError: true });
      return data;
    },
  });
}

/** Adopt that request now, rather than waiting for the watcher. */
export function useHrPortalPickUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ticketId: string; kind: "new" | "leave" }) => {
      const { data } = await hrPortalPickUp({
        body: { ticket_id: input.ticketId, kind: input.kind },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries(),
  });
}
